import React from 'react';
import { TableRow, TableCell, Skeleton } from '@mui/material';
import cx from 'classnames';

// Must match VirtualTableRow's condensation height
const condensationHeigth = 7;

/**
 * Skeleton row component for VirtualTable.
 * Renders placeholder skeleton cells during infinite scroll load-more operations.
 * 
 * IMPORTANT: Structure MUST match VirtualTableRow exactly for:
 * - Consistent row height measurement by virtualizer
 * - Proper alignment of checkbox, menu, custom, and data columns
 * - Correct locked column positioning
 * 
 * @component
 * @since Phase 6 - VirtualTable Enhancement Plan
 */
const SkeletonTableRow = React.memo(React.forwardRef(({
    dataIndex,
    visibleColumns,
    disableItemSelection,
    lockedColumns,
    lockedColumnsHandler,
    menuOptions,
    customColumn,
    density,
    defaultDensity
}, ref) => (
    <TableRow 
        data-index={dataIndex} 
        ref={ref} 
        style={{ backgroundColor: dataIndex % 2 === 0 ? '#f9f9f9' : '#fff' }}
        className="skeleton-row"
    >
        {/* Checkbox cell skeleton - matches VirtualTableRow checkbox cell */}
        {!disableItemSelection && (
            <TableCell 
                padding="checkbox" 
                className={cx(lockedColumns && "locked-columns")}
            >
                <Skeleton 
                    variant="rectangular" 
                    width={20} 
                    height={20} 
                    animation="wave" 
                />
            </TableCell>
        )}
        
        {/* Menu cell skeleton - matches VirtualTableRow menu cell */}
        {menuOptions && (
            <TableCell 
                padding="checkbox" 
                className={cx("context-menu-container", lockedColumns && "locked-columns")}
            >
                <div style={{ display: 'flex', width: '100%', justifyContent: 'center', alignItems: 'center' }}>
                    <Skeleton variant="circular" width={24} height={24} animation="wave" />
                </div>
            </TableCell>
        )}
        
        {/* Custom column cell skeleton - matches VirtualTableRow custom column cell */}
        {customColumn && (
            <TableCell 
                padding="checkbox" 
                className={cx("context-menu-container", lockedColumns && "locked-columns")}
            >
                <div style={{ display: 'flex', width: '100%', justifyContent: 'center', alignItems: 'center' }}>
                    <Skeleton variant="rectangular" width={32} height={24} animation="wave" />
                </div>
            </TableCell>
        )}
        
        {/* Data column skeletons - matches VirtualTableRow data cells exactly */}
        {/* Phase 7: Fixed width matching - use same cell ID pattern and explicit widths */}
        {visibleColumns.map((col, index) => (
            <TableCell
                key={`skeleton-${col.key}`}
                className={cx(lockedColumns && col?.locked && "locked-columns")}
                style={{
                    left: lockedColumns && col?.locked && lockedColumnsHandler(index, visibleColumns, disableItemSelection, "cell"),
                    paddingTop: defaultDensity && ((density - 1) * condensationHeigth),
                    paddingBottom: defaultDensity && ((density - 1) * condensationHeigth),
                    // Phase 7: Match header cell width exactly to prevent layout shifts
                    width: col.width || undefined,
                    minWidth: col.width || undefined,
                }}
                // Phase 7: Use same ID pattern as real cells for consistent measurement
                id={`cell-${col.key}`}
            >
                <Skeleton 
                    variant="text" 
                    // Phase 7: Use 100% width to fill cell (cell width controls size)
                    width="100%"
                    height={24}
                    animation="wave"
                />
            </TableCell>
        ))}
    </TableRow>
)));

SkeletonTableRow.displayName = 'SkeletonTableRow';

export default SkeletonTableRow;
